import React, { ReactNode, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import {
  spacing,
  mediaMax,
  fontWeight,
  fontSize,
} from 'presentations/views/styles';
import {
  blueGrayPale,
  primary,
  whiteFix,
} from 'presentations/views/styles/color';
import Image from 'presentations/views/components/atoms/Image';
import Logo from 'presentations/views/components/atoms/Logo';
import Headline from 'presentations/views/components/atoms/Headline';

import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

export enum BrandContentMode {
  Host = 1,
  Guest = 2,
}

export interface Props {
  brandContentMode: BrandContentMode;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const [slideList, setSlideList] = useState<ReactNode | null>(null);

  useEffect(() => {
    (async () => {
      const { Pagination, Autoplay, EffectFade } = await import('swiper');
      const { Swiper, SwiperSlide } = await import('swiper/react');
      const slides = [];
      if (props.brandContentMode === BrandContentMode.Guest) {
        for (let index = 0; index < 2; index++) {
          const i = index + 1;
          slides.push(
            <SwiperSlide key={`guest-${i}`}>
              <SlideListItem>
                <SlideImage
                  src={`pages/signup/guest_${i}.png`}
                  width={460}
                  height={350}
                  alt=""
                />
                <TitleContainer>
                  <IconLabel>Point</IconLabel>
                  <Title>{t(`guest_${i}_title`)}</Title>
                </TitleContainer>
                <Text>{t(`guest_${i}_text`)}</Text>
              </SlideListItem>
            </SwiperSlide>,
          );
        }
      }
      if (props.brandContentMode === BrandContentMode.Host) {
        for (let index = 0; index < 3; index++) {
          const i = index + 1;
          slides.push(
            <SwiperSlide key={`host-${i}`}>
              <SlideListItem>
                <SlideImage
                  src={`pages/signup/host_${i}.png`}
                  width={460}
                  height={350}
                  alt=""
                />
                <TitleContainer>
                  <IconLabel>{`Step ${i}`}</IconLabel>
                  <Title>{t(`host_${i}_title`)}</Title>
                </TitleContainer>
                <Text>{t(`host_${i}_text`)}</Text>
              </SlideListItem>
            </SwiperSlide>,
          );
        }
      }
      setSlideList(
        <Swiper
          slidesPerView={1}
          effect={'fade'}
          speed={1000}
          loop
          autoplay={{
            delay: 3000,
            disableOnInteraction: false,
          }}
          pagination={{
            clickable: true,
          }}
          modules={[Pagination, Autoplay, EffectFade]}
          className="mySwiper"
        >
          {slides}
        </Swiper>,
      );
    })();
  }, []);

  let title = t('host_title');
  if (props.brandContentMode === BrandContentMode.Guest) {
    title = t('guest_title');
  }

  return (
    <Container>
      <StyledLogo width={140} />
      <TitleHeadline variant="h1">{title}</TitleHeadline>
      <SlideList>{slideList}</SlideList>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const StyledLogo = styled(Logo)`
  margin-bottom: ${spacing(28)};
`;
const TitleHeadline = styled(Headline)`
  color: ${primary};
  font-size: ${fontSize(24)};
  margin-bottom: ${spacing(16)};
`;
const SlideList = styled.div`
  width: 460px;
  ${mediaMax.desktop} {
    width: 320px;
  }
`;
const SlideListItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 460px;
  height: 500px;
  background-color: ${blueGrayPale};
  ${mediaMax.desktop} {
    width: 320px;
    height: 420px;
  }
`;
const SlideImage = styled(Image)`
  margin-bottom: ${spacing(16)};
  ${mediaMax.desktop} {
    width: 320px;
    height: auto;
  }
`;
const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${spacing(16)};
`;
const Title = styled.div`
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(20)};
`;
const IconLabel = styled.div`
  height: fit-content;
  color: ${whiteFix};
  background-color: ${primary};
  border-radius: 50px;
  font-weight: ${fontWeight('bold')};
  padding: ${spacing(4)} ${spacing(8)};
  font-size: ${fontSize(12)};
  margin-right: ${spacing(8)};
`;
const Text = styled.div``;
